import { formatValue, humanizeMeasure, formatFromColumn } from "@/lib/reports/labels";

import type { ChartBuildCtx, ChartOption } from "./context";
import { aggregateByCategory } from "./helpers";

/** pie + donut — donut adds the inner radius + centre callout. */
export function buildPieDonutOption({ viz, data, palette, base, xKey, yKey }: ChartBuildCtx): ChartOption {
  const rolledUp = aggregateByCategory(data.rows, xKey, [yKey], data.columns, false);
  rolledUp.sort((a, b) => Number(b[yKey] ?? 0) - Number(a[yKey] ?? 0));
  const aggregated = rolledUp.map((r, i) => ({
    name: String(r[xKey] ?? ""),
    value: Number(r[yKey] ?? 0),
    // Per-slice palette: ECharts' default would cycle the global
    // color palette per data-item, but several themes / parent
    // ECharts options override that — pinning explicitly here keeps
    // every slice a distinct hue.
    itemStyle: { color: palette[i % palette.length] },
  }));
  const totalValue = aggregated.reduce((acc, s) => acc + s.value, 0);
  // Hide labels on slices smaller than 3% of the total — the label
  // line overlap is unreadable at typical dashboard sizes. The slice
  // is still in the legend + tooltip + colour-coded.
  const LABEL_THRESHOLD_PCT = 3;
  const isDonut = viz.type === "donut";
  const isSingleSlice = aggregated.length === 1;
  const yFmt = formatFromColumn(yKey, data.columns);
  // Donut centre callout — surface the total or top-slice depending
  // on whether the measure is summable. Percent / rate measures get
  // the top slice (the leader by value); counts get the total.
  const centerMeta =
    isSingleSlice
      ? { label: aggregated[0].name, value: aggregated[0].value, caption: aggregated[0].name }
      : yFmt === "percent" || yFmt === "duration_minutes" || yFmt === "duration_seconds"
      ? aggregated.length > 0
        ? { label: aggregated[0].name, value: aggregated[0].value, caption: "Top" }
        : null
      : { label: humanizeMeasure(yKey), value: totalValue, caption: "Total" };
  return {
    ...base,
    tooltip: {
      trigger: "item",
      formatter: (p: { name: string; value: number; percent: number }) =>
        `<b>${p.name}</b><br/>${humanizeMeasure(yKey)}: ${formatValue(p.value, yFmt)}<br/>Share: ${p.percent.toFixed(1)}%`,
    },
    legend: {
      show: !isSingleSlice,
      bottom: 0,
      textStyle: { color: "#6b7280", fontSize: 11 },
      // Truncate long category names in the legend too.
      formatter: (name: string) => (name.length > 22 ? name.slice(0, 21) + "…" : name),
    },
    // Donut centre callout via graphic — ECharts series-level title
    // can't truly centre across the donut hole at all sizes, but a
    // graphic element pinned to chart center works reliably.
    graphic: isDonut && centerMeta
      ? {
          type: "group",
          left: "center",
          top: "40%",
          children: [
            {
              type: "text",
              left: "center",
              top: -6,
              style: {
                text: formatValue(centerMeta.value, yFmt),
                fill: "#111",
                fontSize: 20,
                fontWeight: 700,
                textAlign: "center",
              },
            },
            {
              type: "text",
              left: "center",
              top: 18,
              style: {
                text: centerMeta.caption,
                fill: "#6b7280",
                fontSize: 11,
                textAlign: "center",
              },
            },
          ],
        }
      : undefined,
    series: [
      {
        type: "pie",
        // Shrunk donut/pie radius slightly + pulled centre up so the
        // external slice labels (esp. the bottom one) don't clip
        // under the legend / chart-card footer. Caught in
        // eval-donut-channel-share: "LinkedIn" label was truncated.
        radius: isDonut ? ["40%", "60%"] : "60%",
        center: ["50%", "42%"],
        data: aggregated,
        label: {
          show: !(isDonut && isSingleSlice),
          color: isSingleSlice ? "#fff" : "#374151",
          fontSize: 11,
          fontWeight: isSingleSlice ? 700 : 400,
          position: isSingleSlice ? "inside" : "outside",
          formatter: (p: { name: string; percent: number }) => {
            if (p.percent < LABEL_THRESHOLD_PCT) return "";
            const trimmed = p.name.length > 16 ? p.name.slice(0, 15) + "…" : p.name;
            return `${trimmed}\n${p.percent.toFixed(1)}%`;
          },
        },
        labelLine: {
          length: 8,
          length2: 6,
          // Skip the line for hidden labels (mirrors the label formatter
          // returning "" — otherwise dangling lines stick out).
          show: false,
        },
        // Re-enable for non-tiny slices via a richer per-slice config
        // would be nicer but ECharts treats labelLine.show globally per
          // series; the visual cost of no lines at all is small.
        avoidLabelOverlap: true,
        itemStyle: { borderColor: "#fff", borderWidth: 2 },
      },
    ],
  } as ChartOption;
}
